import React from 'react';
import { Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { appLogo, logoUSKidsHorizontal } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type Props = { onLogout: () => void; onEdit?: () => void };

export function ProfileHeader({ onLogout, onEdit }: Props) {
  const { isTablet, scale } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  return (
    <Stack
      direction="row"
      align="center"
      justify="space-between"
      backgroundColor={isTablet ? colors['0'] : 'transparent'}
      style={[styles.root, { paddingTop: insets.top + scale({ phone: 4, tablet: 0 }) }]}>
      <Image
        source={isTablet ? logoUSKidsHorizontal : appLogo}
        width={scale({ phone: 64, tablet: 166 })}
        height={scale({ phone: 29, tablet: 27 })}
        resizeMode="contain"
      />
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {STRINGS.guardianProfile.title}
      </Text>
      {isTablet ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={STRINGS.guardianProfile.logout.accessibilityLabel}
          onPress={onLogout}
          style={({ pressed }) => [styles.logoutButton, pressed ? styles.pressed : null]}>
          <SvgIcon name="IcProfileLogout" size={scale(20)} />
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.logoutText}>
            {STRINGS.guardianProfile.logout.tablet}
          </Text>
        </Pressable>
      ) : (
        <Stack align="flex-end" style={styles.phoneAction}>
          <Pressable
            onPress={onEdit}
            style={({ pressed }) => [styles.editButton, pressed ? styles.pressed : null]}>
            <SvgIcon name="IcEditAvatar" size={scale(24)} color={guardianColors['10']} />
          </Pressable>
        </Stack>
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    minHeight: scale({ phone: 48, tablet: 64 }),
    paddingHorizontal: scale(20),
    paddingBottom: scale({ phone: 4, tablet: 0 }),
    borderBottomWidth: scale({ phone: 0, tablet: 1 }),
    borderBottomColor: guardianColors['3'],
  },
  title: { color: guardianColors['10'], textAlign: 'center' },
  phoneAction: { width: scale(64) },
  editButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    backgroundColor: 'rgba(255,255,255,0.9)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutButton: {
    width: scale(100),
    height: scale(40),
    marginVertical: scale(4),
    borderRadius: scale(12),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    backgroundColor: colors['0'],
  },
  logoutText: { color: guardianColors.Error },
  pressed: { opacity: 0.68 },
}));
